import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type TranscriptLine = {
  text: string;
  role?: string | null;
};

type Props = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  lines: TranscriptLine[];
  partialText?: string | null;
  partialRole?: string | null;
  emptyText?: string;
};

export function TranscriptionSidebar({
  isOpen,
  setIsOpen,
  lines,
  partialText,
  partialRole,
  emptyText = 'No transcript yet.',
}: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [stickToBottom, setStickToBottom] = useState(true);

  const grouped = useMemo(() => {
    const result: { role?: string | null; text: string; partialText?: string }[] = [];
    let current: { role?: string | null; text: string; partialText?: string } | null = null;

    const pushCurrent = () => {
      if (!current) return;
      if (current.text.trim().length > 0 || (current.partialText && current.partialText.trim().length > 0)) {
        result.push(current);
      }
    };

    for (const line of lines) {
      const trimmed = line.text.trim();
      if (!trimmed) continue;
      if (!current) {
        current = { role: line.role ?? null, text: trimmed };
        continue;
      }
      if (line.role && current.role && line.role !== current.role) {
        pushCurrent();
        current = { role: line.role ?? null, text: trimmed };
      } else {
        current.text = `${current.text} ${trimmed}`.trim();
      }
    }

    if (partialText && partialText.trim()) {
      if (!current) {
        current = { role: partialRole ?? null, text: '', partialText: partialText.trim() };
      } else if (partialRole && current.role && partialRole !== current.role) {
        pushCurrent();
        current = { role: partialRole ?? null, text: '', partialText: partialText.trim() };
      } else {
        current.partialText = partialText.trim();
      }
    }

    pushCurrent();
    return result;
  }, [lines, partialText, partialRole]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setStickToBottom(atBottom);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [grouped.length, partialText, stickToBottom]);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: '64px',
          left: isOpen ? 0 : '-320px',
          width: '320px',
          height: 'calc(100% - 64px)',
          backgroundColor: '#1B1D22',
          color: '#ffffff',
          boxShadow: '2px 0 5px rgba(0,0,0,0.5)',
          borderTopRightRadius: '10px',
          padding: '1rem',
          transition: 'left 0.3s ease',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
          }}
        >
          <div style={{ fontWeight: 600 }}>Transcription</div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close transcription sidebar"
            title="Close"
            style={{
              width: '28px',
              height: '28px',
              border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: '8px',
              backgroundColor: 'rgba(255,255,255,0.06)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            gap: '0.35rem',
          }}
        >
          {grouped.map((line, idx) => (
            <div key={`${idx}-${line.text}`}>
              {line.role ? <strong>{line.role}: </strong> : null}
              {line.text}
              {line.partialText ? (
                <span style={{ opacity: 0.6 }}> {line.partialText}</span>
              ) : null}
            </div>
          ))}
          {!grouped.length && !partialText && (
            <div style={{ opacity: 0.72 }}>
              {emptyText}
            </div>
          )}
        </div>
      </div>

      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          left: isOpen ? '320px' : '0',
          bottom: '24px',
          width: '41px',
          height: '41px',
          borderRadius: '0 8px 8px 0',
          backgroundColor: '#333',
          color: '#fff',
          border: '1px solid #555',
          cursor: 'pointer',
          boxShadow: '2px 0 5px rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          transition: 'left 0.3s ease',
        }}
        aria-label="Toggle transcription sidebar"
        title="Transcription"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="currentColor"
        >
          <path d="M4 4h16v2H4zM4 8h10v2H4zM4 12h16v2H4zM4 16h10v2H4z" />
        </svg>
      </button>
    </>
  );
}
